import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {OverlayElementResource} from '../resource/overlay-element.resource';
import {OverlayElement} from '../interfaces/overlay-element.interface';
import {MapStateService} from './map-state.service';
import {MapService} from './map.service';
import {
  OverlayElementStateData,
  StatelessOverlayElement
} from '../../../../../maporium/src/app/entities/types/overlay-element-state-data';

@Injectable({providedIn: 'root'})
export class OverlayElementApiService {
  constructor(
    private resource: OverlayElementResource,
    private mapService: MapService,
    private mapStateService: MapStateService
  ) {
  }

  getAllForMap(mapId: string): Observable<OverlayElement[]> {
    return this.resource.getAllForMap(mapId);
  }

  getOne(id: string): Observable<OverlayElement> {
    return this.resource.getOne(id);
  }

  create(element: OverlayElement): Observable<OverlayElement> {
    return this.resource.create(element);
  }

  update(element: OverlayElement): Observable<OverlayElement> {
    const currentLocalState = this.mapStateService.getCurrentLocalState();
    const isDefaultState = currentLocalState?.id === undefined;

    if (!isDefaultState) {
      return this.updateWithState(element, currentLocalState);
    }

    return this.resource.update(element);
  }

  updatePosition(id: string, x: number, y: number): Observable<OverlayElement> {
    const currentSelectedState = this.mapStateService.getLocalState();
    const currentMapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    const isDefaultState = currentSelectedState === null || currentSelectedState[currentMapId]?.id === undefined;

    if (!isDefaultState) {
      return this.resource.updatePosition(id, x, y, currentSelectedState[currentMapId].id);
    }

    return this.resource.updatePosition(id, x, y);
  }

  delete(id: string): Observable<any> {
    return this.resource.delete(id);
  }

  restore(id: string): Observable<OverlayElement> {
    return this.resource.restore(id);
  }

  batchRestore(ids: string[]): Observable<OverlayElement[]> {
    return this.resource.batchRestore(ids);
  }

  resetElement(id: string, stateId: string): Observable<OverlayElement> {
    return this.resource.resetElement(id, stateId);
  }

  private updateWithState(element: OverlayElement, currentLocalState: any): Observable<OverlayElement> {
    const elementCopy = structuredClone(element);
    const existingStates = elementCopy.states || [];
    const hasCurrentState = existingStates.find((s: OverlayElementStateData) => s.stateId === currentLocalState.id);

    const stateElement: StatelessOverlayElement = {
      id: elementCopy.id,
      name: elementCopy.name,
      x: elementCopy.x,
      y: elementCopy.y,
      width: elementCopy.width,
      height: elementCopy.height,
      rotation: elementCopy.rotation,
      imageUrl: elementCopy.imageUrl,
      type: elementCopy.type,
      properties: elementCopy.properties,
      description: elementCopy.description,
      excludedFromState: elementCopy.excludedFromState
    };

    if (!hasCurrentState) {
      existingStates.push({
        stateId: currentLocalState.id,
        element: stateElement
      });
    } else {
      existingStates.forEach((state: OverlayElementStateData) => {
        if (state.stateId === currentLocalState.id) {
          state.element = stateElement;
        }
      });
    }

    const payload = {
      id: element.id,
      states: existingStates
    } as unknown as OverlayElement;

    return this.resource.update(payload);
  }
}
